import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as faStarSolid, faCheck, faTimes, faTrash, faCommentSlash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";

import CustomModal from "../../Components/CustomModal";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import Loader from '../../Components/Loader';
import FormModal from "../../Components/FormModal";
import Header from "../../Components/Header";

import "./style.css";

import usePagination from "../../Hooks/usePagination";
import useSearch from "../../Hooks/useSearch";
import {
  useGetPendingReviewsQuery,
  useGetAllReviewsQuery,
  useGetReviewStatsQuery,
  useApproveReviewMutation,
  useRejectReviewMutation,
  useDeleteReviewMutation
} from "../../Redux/Apis/VendorReview";
import { dateFormatter } from "../../Utils";

// Default placeholder for user avatars
const PLACEHOLDER_IMAGE = "https://ui-avatars.com/api/?background=e0e0e0&color=666&name=U";

const TABS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ALL: 'all'
};

export const ReviewApprovals = () => {
  const [activeTab, setActiveTab] = useState(TABS.PENDING);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showApproveSuccessModal, setShowApproveSuccessModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectSuccessModal, setShowRejectSuccessModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const [search, debouncedSearch, onSearch] = useSearch();
  const { page, perPage, onNext, onPrevious, resetPage } = usePagination();

  const [approveReview, { isLoading: isApproving }] = useApproveReviewMutation();
  const [rejectReview, { isLoading: isRejecting }] = useRejectReviewMutation();
  const [deleteReview, { isLoading: isDeleting }] = useDeleteReviewMutation();

  // Fetch stats
  const { data: statsData } = useGetReviewStatsQuery();

  // Fetch reviews based on active tab
  const queryParams = {
    page,
    rowsPerPage: perPage,
    search: debouncedSearch,
    ...(activeTab !== TABS.ALL && { status: activeTab })
  };

  const { data: pendingData, isLoading: isPendingLoading, refetch: refetchPending } = useGetPendingReviewsQuery(
    { page, rowsPerPage: perPage, search: debouncedSearch },
    { skip: activeTab !== TABS.PENDING }
  );

  const { data: allReviewsData, isLoading: isAllLoading, refetch: refetchAll } = useGetAllReviewsQuery(
    queryParams,
    { skip: activeTab === TABS.PENDING }
  );

  const reviews = activeTab === TABS.PENDING ? pendingData?.data : allReviewsData?.data;
  const isLoading = activeTab === TABS.PENDING ? isPendingLoading : isAllLoading;
  const stats = statsData?.data || { pending: 0, approved: 0, rejected: 0, total: 0 };

  useEffect(() => {
    document.title = 'The Turn At | Review Approvals';
  }, []);

  useEffect(() => {
    resetPage();
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleApproveClick = (review) => {
    setSelectedReview(review);
    setShowApproveModal(true);
  };

  const handleRejectClick = (review) => {
    setSelectedReview(review);
    setShowRejectModal(true);
  };

  const handleApproveConfirm = async () => {
    try {
      await approveReview(selectedReview._id).unwrap();
      setShowApproveModal(false);
      setShowApproveSuccessModal(true);
      refetchPending();
      refetchAll();
    } catch (error) {
      console.error('Error approving review:', error);
    }
  };

  const handleRejectConfirm = async () => {
    setShowRejectModal(false);
    setReasonOpen(true);
  };

  const onConfirmReject = async (data) => {
    try {
      await rejectReview({
        reviewId: selectedReview._id,
        reason: data.reason
      }).unwrap();
      setReasonOpen(false);
      setShowRejectSuccessModal(true);
      refetchPending();
      refetchAll();
    } catch (error) {
      console.error('Error rejecting review:', error);
    }
  };

  const handleDeleteClick = (review) => {
    setSelectedReview(review);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteReview(selectedReview._id).unwrap();
      setShowDeleteModal(false);
      setShowDeleteSuccessModal(true);
      refetchPending();
      refetchAll();
    } catch (error) {
      console.error('Error removing review:', error);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <FontAwesomeIcon
            key={star}
            icon={star <= rating ? faStarSolid : faStarRegular}
            className={`star ${star <= rating ? '' : 'empty'}`}
          />
        ))}
      </div>
    );
  };

  const renderReviewCard = (review) => (
    <div key={review._id} className="review-card">
      <div className="review-header">
        <div className="reviewer-info">
          <img
            src={review.reviewer?.picture || PLACEHOLDER_IMAGE}
            alt={review.reviewer?.name}
            className="reviewer-avatar"
            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
          />
          <div>
            <p className="reviewer-name">{review.reviewer?.name || 'Anonymous'}</p>
            <p className="reviewer-email">{review.reviewer?.email}</p>
          </div>
        </div>
        <div className="rating-display">
          {renderStars(review.rating)}
          <span className="rating-number">{review.rating}/5</span>
        </div>
      </div>

      <div className="vendor-info">
        <img
          src={review.vendor?.picture || PLACEHOLDER_IMAGE}
          alt={review.vendor?.name}
          className="vendor-avatar"
          onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
        />
        <div>
          <p className="vendor-label">Reviewing vendor:</p>
          <p className="vendor-name">{review.vendor?.name || 'Unknown Vendor'}</p>
        </div>
      </div>

      <div className="review-content">
        <p className="review-text">{review.review}</p>
      </div>

      {review.status === 'rejected' && review.rejectionReason && (
        <div className="rejection-reason">
          <p className="rejection-reason-label">Rejection Reason:</p>
          <p className="rejection-reason-text">{review.rejectionReason}</p>
        </div>
      )}

      <div className="review-meta">
        <div>
          <span className="review-date">
            Submitted: {dateFormatter(review.createdAt)}
          </span>
          {review.reviewedAt && (
            <span className="review-date" style={{ marginLeft: 16 }}>
              Reviewed: {dateFormatter(review.reviewedAt)}
            </span>
          )}
        </div>

        {review.status === 'pending' ? (
          <div className="review-actions">
            <button
              className="btn-approve"
              onClick={() => handleApproveClick(review)}
              disabled={isApproving || isRejecting}
            >
              <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6 }} />
              Approve
            </button>
            <button
              className="btn-reject"
              onClick={() => handleRejectClick(review)}
              disabled={isApproving || isRejecting}
            >
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: 6 }} />
              Reject
            </button>
          </div>
        ) : (
          <div className="review-actions">
            <span className={`status-badge status-${review.status}`}>
              {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
            </span>
            <button
              className="btn-reject"
              onClick={() => handleDeleteClick(review)}
              disabled={isDeleting}
              title="Permanently remove this review"
            >
              <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="empty-state">
      <FontAwesomeIcon icon={faCommentSlash} className="empty-state-icon" />
      <h3>No reviews to display</h3>
      <p>
        {activeTab === TABS.PENDING
          ? 'All reviews have been moderated. Great job!'
          : `No ${activeTab} reviews found.`}
      </p>
    </div>
  );

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <Header
                  title="Vendor Review Moderation"
                  search={search}
                  onSearch={onSearch}
                />

                {/* Stats Cards */}
                <div className="stats-container">
                  <div className="stat-card">
                    <p className="stat-number pending">{stats.pending}</p>
                    <p className="stat-label">Pending Review</p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-number approved">{stats.approved}</p>
                    <p className="stat-label">Approved</p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-number rejected">{stats.rejected}</p>
                    <p className="stat-label">Rejected</p>
                  </div>
                  <div className="stat-card">
                    <p className="stat-number total">{stats.total}</p>
                    <p className="stat-label">Total Reviews</p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="tabs-container">
                  <button
                    className={`tab-btn ${activeTab === TABS.PENDING ? 'active' : ''}`}
                    onClick={() => handleTabChange(TABS.PENDING)}
                  >
                    Pending
                    <span className="tab-count">{stats.pending}</span>
                  </button>
                  <button
                    className={`tab-btn ${activeTab === TABS.APPROVED ? 'active' : ''}`}
                    onClick={() => handleTabChange(TABS.APPROVED)}
                  >
                    Approved
                    <span className="tab-count">{stats.approved}</span>
                  </button>
                  <button
                    className={`tab-btn ${activeTab === TABS.REJECTED ? 'active' : ''}`}
                    onClick={() => handleTabChange(TABS.REJECTED)}
                  >
                    Rejected
                    <span className="tab-count">{stats.rejected}</span>
                  </button>
                  <button
                    className={`tab-btn ${activeTab === TABS.ALL ? 'active' : ''}`}
                    onClick={() => handleTabChange(TABS.ALL)}
                  >
                    All
                    <span className="tab-count">{stats.total}</span>
                  </button>
                </div>

                {/* Reviews List */}
                {isLoading ? (
                  <Loader />
                ) : reviews?.length > 0 ? (
                  <div className="review-approvals">
                    {reviews.map(renderReviewCard)}
                  </div>
                ) : (
                  renderEmptyState()
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>

      {/* Approve Confirmation Modal */}
      <CustomModal
        show={showApproveModal}
        close={() => setShowApproveModal(false)}
        heading="Approve Review"
        para={`Are you sure you want to approve this ${selectedReview?.rating}-star review? It will be visible to all users.`}
        success
        onConfirm={handleApproveConfirm}
        confirmText={isApproving ? "Approving..." : "Approve"}
        cancelText="Cancel"
      />

      {/* Approve Success Modal */}
      <CustomModal
        show={showApproveSuccessModal}
        close={() => setShowApproveSuccessModal(false)}
        heading="Review Approved"
        para="The review has been approved and is now visible to users."
        success
      />

      {/* Reject Confirmation Modal */}
      <CustomModal
        show={showRejectModal}
        close={() => setShowRejectModal(false)}
        heading="Reject Review"
        para={`Are you sure you want to reject this review? The reviewer will be notified.`}
        error
        onConfirm={handleRejectConfirm}
        confirmText="Continue"
        cancelText="Cancel"
      />

      {/* Reject Reason Modal */}
      <FormModal
        open={reasonOpen}
        closeModal={() => setReasonOpen(false)}
        title="Rejection Reason"
        onConfirm={onConfirmReject}
        confirmText={isRejecting ? "Rejecting..." : "Reject Review"}
        inputs={[
          {
            type: 'textarea',
            label: 'Reason for Rejection',
            name: 'reason',
            placeholder: 'Explain why this review is being rejected...',
            required: true
          }
        ]}
      />

      {/* Reject Success Modal */}
      <CustomModal
        show={showRejectSuccessModal}
        close={() => setShowRejectSuccessModal(false)}
        heading="Review Rejected"
        para="The review has been rejected and the reviewer has been notified."
        success
      />

      {/* Remove (delete) Confirmation Modal */}
      <CustomModal
        show={showDeleteModal}
        close={() => setShowDeleteModal(false)}
        heading="Remove Review"
        para="Permanently remove this review? It disappears from the vendor's profile and their star rating recalculates. This cannot be undone."
        error
        onConfirm={handleDeleteConfirm}
        confirmText={isDeleting ? "Removing..." : "Remove"}
        cancelText="Cancel"
      />

      {/* Remove Success Modal */}
      <CustomModal
        show={showDeleteSuccessModal}
        close={() => setShowDeleteSuccessModal(false)}
        heading="Review Removed"
        para="The review has been removed and the vendor's rating has been updated."
        success
      />
    </>
  );
};

export default ReviewApprovals;

